import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Grid,
  Card,
  CardMedia,
  Chip,
  Divider,
  Avatar,
  Skeleton,
  Stack,
  Dialog,
  IconButton,
  Box,
  Typography,
  Zoom,
  Slide,
  Fade,
} from "@mui/material";
import { keyframes } from "@mui/system";

// Icons
import {
  ArrowBack as ArrowBackIcon,
  Category as CategoryIcon,
  LocationOn as LocationOnIcon,
  Description as DescriptionIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Close as CloseIcon,
  Sell as SellIcon,
  ShoppingCart as ShoppingCartIcon,
  VerifiedUser as VerifiedUserIcon,
  Edit as EditIcon,
} from "@mui/icons-material";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import BASE_URL from "BASE_URL";

// ==========================================
// 🔥 3D Animations 🔥
// ==========================================
const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0px); }
`;

const pulseGlow = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(26, 115, 232, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(26, 115, 232, 0); }
  100% { box-shadow: 0 0 0 0 rgba(26, 115, 232, 0); }
`;

function PostDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);

  // Modern 3D Theme Colors
  const themeColors = {
    primaryGradient: "linear-gradient(135deg, #1A73E8 0%, #0D47A1 100%)",
    accentGradient: "linear-gradient(135deg, #FF416C 0%, #FF4B2B 100%)",
    cardBg: "linear-gradient(145deg, #ffffff, #f7f9fc)",
    boxShadow3D: "6px 6px 15px #e1e5ee, -6px -6px 15px #ffffff",
    hoverShadow3D: "10px 10px 25px rgba(0,0,0,0.1), -8px -8px 20px #ffffff",
  };

  useEffect(() => {
    fetchPostDetails();
  }, [id]);

  const fetchPostDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/admin/postdetails?id=${id}`);
      if (response.data.success) {
        setPost(response.data.data[0]);
        if (response.data.data[0].postImage?.length > 0) {
          setSelectedImage(response.data.data[0].postImage[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching post:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return {
          color: "#fff",
          bg: "linear-gradient(135deg, #00b09b, #96c93d)",
          icon: <CheckCircleIcon fontSize="small" />,
        };
      case "pending":
        return {
          color: "#fff",
          bg: "linear-gradient(135deg, #f12711, #f5af19)",
          icon: <PendingIcon fontSize="small" />,
        };
      default:
        return {
          color: "#fff",
          bg: "linear-gradient(135deg, #cb2d3e, #ef473a)",
          icon: <CancelIcon fontSize="small" />,
        };
    }
  };

  if (loading)
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox mt={3} p={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <Skeleton variant="rounded" height={500} sx={{ borderRadius: "20px" }} />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Skeleton variant="rounded" height={500} sx={{ borderRadius: "20px" }} />
            </Grid>
          </Grid>
        </MDBox>
      </DashboardLayout>
    );

  if (!post)
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <Zoom in={true}>
            <Card
              sx={{
                p: 4,
                textAlign: "center",
                background: themeColors.cardBg,
                boxShadow: themeColors.boxShadow3D,
                borderRadius: "20px",
              }}
            >
              <MDTypography color="error" variant="h5" fontWeight="bold" mb={2}>
                Post Not Found
              </MDTypography>
              <MDButton variant="gradient" color="info" onClick={() => navigate(-1)}>
                Go Back
              </MDButton>
            </Card>
          </Zoom>
        </MDBox>
      </DashboardLayout>
    );

  const statusInfo = getStatusConfig(post.status);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox mt={2} mb={4}>
        {/* --- Top Floating Header --- */}
        <Fade in={true} timeout={500}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3} px={1}>
            <MDButton
              variant="contained"
              size="small"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIcon />}
              sx={{
                bgcolor: "#fff",
                color: "#333",
                borderRadius: "10px",
                boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
                textTransform: "none",
                "&:hover": {
                  transform: "translateX(-4px)",
                  bgcolor: "#f8f9fa",
                  boxShadow: "0 6px 15px rgba(0,0,0,0.12)",
                },
              }}
            >
              Back
            </MDButton>

            <MDButton
              variant="gradient"
              color="warning"
              size="small"
              onClick={() => navigate(`/edit-post/${post._id}`)}
              startIcon={<EditIcon />}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                boxShadow: "0 4px 10px rgba(255,152,0,0.3)",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 15px rgba(255,152,0,0.4)",
                },
              }}
            >
              Edit Post
            </MDButton>

            <Chip
              icon={statusInfo.icon}
              label={post.status?.toUpperCase()}
              size="small"
              sx={{
                background: statusInfo.bg,
                color: statusInfo.color,
                fontWeight: "bold",
                padding: "16px 8px",
                borderRadius: "10px",
                boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                animation: `${pulseGlow} 2s infinite, ${floatAnimation} 3s ease-in-out infinite`,
                "& .MuiChip-icon": { color: "#fff" },
              }}
            />
          </MDBox>
        </Fade>

        <Grid container spacing={3}>
          {/* ================= LEFT COLUMN: 3D Gallery & Description ================= */}
          <Grid item xs={12} lg={6}>
            {" "}
            {/* Note: lg=5 or 6 based on your layout, matching it with right side */}
            <Slide direction="right" in={true} timeout={600}>
              <Stack spacing={4}>
                {/* 1. Premium 3D Main Image Showcase */}
                <Card
                  sx={{
                    background: "linear-gradient(145deg, #ffffff, #f7f9fc)",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                    borderRadius: "24px",
                    p: 2, // Inner padding for the frame
                    border: "1px solid rgba(255,255,255,0.8)",
                    overflow: "visible",
                    transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <Box sx={{ position: "relative" }}>
                    {/* Glowing Sell/Buy Tag */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 12,
                        left: 12,
                        background:
                          post.post_type === "sell"
                            ? "linear-gradient(135deg, #FF416C 0%, #FF4B2B 100%)"
                            : "linear-gradient(135deg, #1A73E8 0%, #0D47A1 100%)",
                        color: "#fff",
                        padding: "8px 16px",
                        borderRadius: "14px",
                        fontWeight: "900",
                        boxShadow:
                          post.post_type === "sell"
                            ? "0 8px 20px rgba(255, 65, 108, 0.4)"
                            : "0 8px 20px rgba(26, 115, 232, 0.4)",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        zIndex: 10,
                        animation: `${floatAnimation} 4s ease-in-out infinite`,
                      }}
                    >
                      {post.post_type === "sell" ? (
                        <SellIcon fontSize="small" sx={{ fontSize: "18px" }} />
                      ) : (
                        <ShoppingCartIcon fontSize="small" sx={{ fontSize: "18px" }} />
                      )}
                      <Typography variant="caption" fontWeight="bold" sx={{ letterSpacing: "1px" }}>
                        {post.post_type?.toUpperCase()}
                      </Typography>
                    </Box>

                    {/* Image Stage (Frosty Glass Effect) */}
                    <Box
                      sx={{
                        bgcolor: "rgba(255, 255, 255, 0.6)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255, 255, 255, 0.9)",
                        borderRadius: "20px",
                        overflow: "hidden",
                        boxShadow: "inset 0 4px 15px rgba(0,0,0,0.02)",
                        height: 420,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 2,
                      }}
                    >
                      <CardMedia
                        component="img"
                        image={selectedImage || post.postImage?.[0]}
                        sx={{
                          height: "100%",
                          width: "100%",
                          objectFit: "contain",
                          cursor: "zoom-in",
                          transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                          filter: "drop-shadow(0px 10px 20px rgba(0,0,0,0.15))", // Image ke piche halka shadow
                          "&:hover": { transform: "scale(1.08)" },
                        }}
                        onClick={() => setImageDialogOpen(true)}
                      />
                    </Box>

                    {/* 3D Thumbnails Showcase */}
                    {post.postImage?.length > 1 && (
                      <MDBox
                        mt={2.5}
                        p={1.5}
                        display="flex"
                        gap={2}
                        sx={{
                          overflowX: "auto",
                          bgcolor: "#f8f9fa",
                          borderRadius: "16px",
                          border: "1px solid rgba(0,0,0,0.03)",
                        }}
                      >
                        {post.postImage.map((img, idx) => (
                          <Box
                            key={idx}
                            component="img"
                            src={img}
                            onClick={() => setSelectedImage(img)}
                            sx={{
                              width: 65,
                              height: 65,
                              borderRadius: "12px",
                              objectFit: "cover",
                              cursor: "pointer",
                              bgcolor: "#fff",
                              padding: "2px", // Like an Instagram story border space
                              boxShadow:
                                selectedImage === img
                                  ? "0 8px 16px rgba(26, 115, 232, 0.3)"
                                  : "0 4px 8px rgba(0,0,0,0.08)",
                              border:
                                selectedImage === img
                                  ? "3px solid #1A73E8"
                                  : "3px solid transparent",
                              transform: selectedImage === img ? "translateY(-4px)" : "none",
                              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                              "&:hover": {
                                transform: "translateY(-4px) scale(1.05)",
                                boxShadow: "0 10px 20px rgba(0,0,0,0.12)",
                              },
                            }}
                          />
                        ))}
                      </MDBox>
                    )}
                  </Box>
                </Card>

                {/* 2. Premium Description Card */}
                <Card
                  sx={{
                    background: "linear-gradient(145deg, #ffffff, #f7f9fc)",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                    borderRadius: "24px",
                    p: 3.5,
                    border: "1px solid rgba(255,255,255,0.8)",
                    transition: "all 0.4s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 15px 35px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <MDBox display="flex" alignItems="center" mb={2}>
                    <Box
                      sx={{
                        width: 35,
                        height: 35,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        background: "linear-gradient(135deg, #1A73E8 0%, #0D47A1 100%)",
                        borderRadius: "12px",
                        color: "#fff",
                        mr: 1,
                        boxShadow: "0 6px 15px rgba(26, 115, 232, 0.3)",
                      }}
                    >
                      <DescriptionIcon fontSize="small" />
                    </Box>
                    <Typography variant="h5" fontWeight="900" color="text.primary">
                      Description
                    </Typography>
                  </MDBox>

                  <Divider sx={{ mb: 2.5, borderStyle: "dashed" }} />

                  <Box
                    sx={{
                      p: 2.5,
                      bgcolor: "rgba(255,255,255,0.6)",
                      borderRadius: "16px",
                      border: "1px solid rgba(0,0,0,0.03)",
                    }}
                  >
                    <Typography
                      variant="body1" // Changed to body1 for better paragraph readability
                      color="text.secondary"
                      fontWeight="500"
                      sx={{
                        lineHeight: 1.8,
                        whiteSpace: "pre-wrap",
                        fontSize: "1rem",
                      }}
                    >
                      {post.description || "No description provided for this product."}
                    </Typography>
                  </Box>
                </Card>
              </Stack>
            </Slide>
          </Grid>

          {/* ================= RIGHT COLUMN: Details & Seller Info ================= */}
          <Grid item xs={12} lg={6}>
            <Slide direction="up" in={true} timeout={800}>
              <Stack spacing={4}>
                {/* 1. Title, Price & Details Card */}
                <Card
                  sx={{
                    background: "linear-gradient(145deg, #ffffff, #f7f9fc)",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                    borderRadius: "24px",
                    p: 3.5,
                    border: "1px solid rgba(255,255,255,0.8)",
                    transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <Typography
                    variant="h3"
                    fontWeight="900"
                    color="text.primary"
                    mb={3}
                    sx={{ letterSpacing: "-0.5px" }}
                  >
                    {post.title}
                  </Typography>

                  <Grid container spacing={2} mb={3}>
                    {/* Premium Price Box */}
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          background: "linear-gradient(135deg, #1A73E8 0%, #0D47A1 100%)",
                          borderRadius: "20px",
                          p: 2.5,
                          color: "#fff",
                          boxShadow:
                            "0 10px 20px rgba(26, 115, 232, 0.3), inset 0 2px 5px rgba(255,255,255,0.2)",
                          position: "relative",
                          overflow: "hidden",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-4px) scale(1.02)",
                            boxShadow: "0 15px 25px rgba(26, 115, 232, 0.4)",
                          },
                        }}
                      >
                        {/* Background subtle circle for design */}
                        <Box
                          sx={{
                            position: "absolute",
                            right: -20,
                            top: -20,
                            width: 80,
                            height: 80,
                            borderRadius: "50%",
                            background: "rgba(255,255,255,0.1)",
                          }}
                        />

                        <Typography
                          variant="caption"
                          sx={{
                            opacity: 0.85,
                            letterSpacing: "1.5px",
                            display: "block",
                            mb: 0.5,
                            fontWeight: "bold",
                          }}
                        >
                          TOTAL PRICE
                        </Typography>
                        <Typography variant="h4" fontWeight="900" sx={{ color: "#fff" }}>
                          ₹{post.price?.toLocaleString("en-IN")}&nbsp;/&nbsp;
                          <span>{post.unitType}</span>
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Premium Quantity Box */}
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          bgcolor: "#ffffff",
                          borderRadius: "20px",
                          p: 2.5,
                          boxShadow: "0 8px 16px rgba(0,0,0,0.04)",
                          border: "1px solid rgba(0,0,0,0.06)",
                          position: "relative",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-4px) scale(1.02)",
                            boxShadow: "0 12px 20px rgba(0,0,0,0.08)",
                            borderColor: "rgba(26, 115, 232, 0.3)",
                          },
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            letterSpacing: "1.5px",
                            display: "block",
                            mb: 0.5,
                            fontWeight: "bold",
                          }}
                        >
                          AVAILABLE QUANTITY
                        </Typography>
                        <Typography variant="h4" fontWeight="900" color="text.primary">
                          {post.quantity}{" "}
                          <Typography
                            component="span"
                            variant="h6"
                            color="text.secondary"
                            fontWeight="bold"
                          >
                            {post.unitType}
                          </Typography>
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2.5, borderStyle: "dashed" }} />

                  {/* Location & Category as modern rows */}
                  <Stack spacing={2}>
                    <MDBox
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 1.5,
                        bgcolor: "#f8f9fa",
                        borderRadius: "16px",
                      }}
                    >
                      <Box
                        sx={{
                          p: 1,
                          bgcolor: "#fff",
                          borderRadius: "10px",
                          display: "flex",
                          mr: 2,
                          boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                        }}
                      >
                        <LocationOnIcon sx={{ color: "#1A73E8" }} />
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          fontWeight="bold"
                        >
                          LOCATION
                        </Typography>
                        <Typography variant="button" color="text.primary" fontWeight="bold">
                          {post.location}
                        </Typography>
                      </Box>
                    </MDBox>

                    <MDBox
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 1.5,
                        bgcolor: "#f8f9fa",
                        borderRadius: "16px",
                      }}
                    >
                      <Box
                        sx={{
                          p: 1,
                          bgcolor: "#fff",
                          borderRadius: "10px",
                          display: "flex",
                          mr: 2,
                          boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                        }}
                      >
                        <CategoryIcon sx={{ color: "#FF4B2B" }} />
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          fontWeight="bold"
                        >
                          CATEGORY
                        </Typography>
                        <Typography variant="button" color="text.primary" fontWeight="bold">
                          {post.childCategory?.name || "Uncategorized"}
                        </Typography>
                      </Box>
                    </MDBox>
                  </Stack>
                </Card>

                {/* 2. Seller Information Card */}
                <Card
                  sx={{
                    background: "linear-gradient(145deg, #ffffff, #eaf0f6)",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                    borderRadius: "24px",
                    p: 3.5,
                    position: "relative",
                    overflow: "hidden",
                    border: "1px solid rgba(255,255,255,0.8)",
                    transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  {/* Decorative Background Shape */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      width: 200,
                      height: 200,
                      background: "linear-gradient(135deg, #1A73E8 0%, #0D47A1 100%)",
                      opacity: 0.04,
                      borderBottomLeftRadius: "100%",
                      zIndex: 0,
                    }}
                  />

                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={3.5}
                    position="relative"
                    zIndex={1}
                  >
                    <MDBox display="flex" alignItems="center" gap={1.5}>
                      <Typography variant="h5" fontWeight="900" color="text.primary">
                        Seller Info
                      </Typography>
                      <VerifiedUserIcon
                        sx={{
                          color: "#4CAF50",
                          fontSize: 26,
                          filter: "drop-shadow(0px 2px 4px rgba(76, 175, 80, 0.4))",
                        }}
                      />
                    </MDBox>

                    {/* View Profile Button */}
                    <MDButton
                      variant="gradient"
                      color="info"
                      size="small"
                      onClick={() => navigate(`/users-details/${post.user?._id}`)}
                      sx={{
                        borderRadius: "12px",
                        padding: "8px 16px",
                        textTransform: "none",
                        fontWeight: "bold",
                        boxShadow: "0 6px 15px rgba(26, 115, 232, 0.25)",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 8px 20px rgba(26, 115, 232, 0.4)",
                        },
                      }}
                    >
                      View Profile
                    </MDButton>
                  </MDBox>

                  <MDBox display="flex" alignItems="center" mb={3.5} position="relative" zIndex={1}>
                    <Avatar
                      src={post.user?.profile_photo}
                      sx={{
                        width: 75,
                        height: 75,
                        mr: 2,
                        boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
                        border: "4px solid #fff",
                        background: "#fff",
                      }}
                    />
                    <Box>
                      <Typography
                        variant="h4"
                        fontWeight="900"
                        color="text.primary"
                        sx={{ lineHeight: 1.2 }}
                      >
                        {post.user?.name}
                      </Typography>
                      <Typography
                        variant="button"
                        color="primary"
                        fontWeight="bold"
                        sx={{ letterSpacing: "0.5px" }}
                      >
                        {post.user?.company_name || "Independent Seller"}
                      </Typography>
                    </Box>
                  </MDBox>

                  <Stack spacing={2} position="relative" zIndex={1}>
                    {[
                      {
                        icon: <EmailIcon fontSize="small" />,
                        text: post.user?.email_id || "No Email",
                        label: "Email Address",
                      },
                      {
                        icon: <PhoneIcon fontSize="small" />,
                        text: post.user?.mobile_number || "No Phone",
                        label: "Contact Number",
                      },
                      {
                        icon: <BusinessIcon fontSize="small" />,
                        text: post.user?.business_type || "N/A",
                        label: "Business Type",
                      },
                    ].map((item, index) => (
                      <MDBox
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          p: 1.5,
                          bgcolor: "rgba(255, 255, 255, 0.7)",
                          backdropFilter: "blur(10px)",
                          border: "1px solid rgba(255, 255, 255, 0.9)",
                          borderRadius: "16px",
                          boxShadow: "0 4px 10px rgba(0,0,0,0.02)",
                          transition: "all 0.2s",
                          "&:hover": {
                            transform: "translateX(8px)",
                            boxShadow: "0 6px 15px rgba(0,0,0,0.06)",
                            bgcolor: "rgba(255, 255, 255, 1)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 42,
                            height: 42,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            background: "linear-gradient(135deg, #ffffff 0%, #f0f3f8 100%)",
                            boxShadow:
                              "inset 0 2px 4px rgba(255,255,255,1), 0 2px 6px rgba(0,0,0,0.05)",
                            borderRadius: "12px",
                            color: "#1A73E8",
                            mr: 2.5,
                          }}
                        >
                          {item.icon}
                        </Box>
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight="bold"
                            display="block"
                            sx={{ mb: -0.2, letterSpacing: "0.5px" }}
                          >
                            {item.label}
                          </Typography>
                          <Typography variant="h6" fontWeight="600" color="text.primary">
                            {item.text}
                          </Typography>
                        </Box>
                      </MDBox>
                    ))}
                  </Stack>
                </Card>
              </Stack>
            </Slide>
          </Grid>
        </Grid>
      </MDBox>

      {/* --- Image Viewer Modal --- */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth="lg"
        TransitionComponent={Zoom}
        TransitionProps={{ timeout: 300 }}
        PaperProps={{
          sx: {
            background: "transparent",
            boxShadow: "none",
            overflow: "visible",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <IconButton
            onClick={() => setImageDialogOpen(false)}
            sx={{
              position: "absolute",
              top: -15,
              right: -15,
              color: "#fff",
              background: themeColors.accentGradient,
              boxShadow: "0 4px 10px rgba(255, 65, 108, 0.4)",
              zIndex: 10,
              "&:hover": { transform: "scale(1.1)", background: themeColors.accentGradient },
              transition: "all 0.2s ease",
            }}
          >
            <CloseIcon />
          </IconButton>
          <img
            src={selectedImage}
            alt="Preview"
            style={{
              maxWidth: "100%",
              maxHeight: "85vh",
              objectFit: "contain",
              borderRadius: "16px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
            }}
          />
        </Box>
      </Dialog>
    </DashboardLayout>
  );
}

export default PostDetails;
